import React, { useState, useEffect } from 'react';
import { Layout, Card, Row, Col, Button, Table, Input, Select, notification, Tag, Tabs } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { getSavedHubspotDeals, getSavedWellskyRecords } from '../../services/index.js';

const { Content } = Layout;
const { Option } = Select;
const { TabPane } = Tabs;

const SavedDataPage = () => {
  // HubSpot data state
  const [hubspotData, setHubspotData] = useState([]);
  const [hubspotLoading, setHubspotLoading] = useState(false);
  const [hubspotPagination, setHubspotPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });

  // WellSky data state
  const [wellskyData, setWellskyData] = useState([]);
  const [wellskyLoading, setWellskyLoading] = useState(false);
  const [wellskyPagination, setWellskyPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0
  });

  // Filters
  const [hubspotFilters, setHubspotFilters] = useState({
    search: '',
    salesRep: '',
    dealStage: '',
    commissionEligible: '',
    clientType: '',
    pipeline: ''
  });

  const [wellskyFilters, setWellskyFilters] = useState({
    search: '',
    salesRep: '',
    serviceType: '',
    agencySubdomain: '',
    marketer: ''
  });

  // Load HubSpot data
  const loadHubspotData = async (page = 1, filters = {}) => {
    setHubspotLoading(true);
    try {
      const params = {
        page,
        limit: hubspotPagination.pageSize,
        ...filters
      };

      const result = await getSavedHubspotDeals(params);
      setHubspotData(result.deals);
      setHubspotPagination(prev => ({
        ...prev,
        current: page,
        total: result.pagination.total
      }));
    } catch (error) {
      notification.error({
        message: 'Error Loading HubSpot Data',
        description: error.message,
        placement: 'topRight'
      });
    } finally {
      setHubspotLoading(false);
    }
  };

  // Load WellSky data
  const loadWellskyData = async (page = 1, filters = {}) => {
    setWellskyLoading(true);
    try {
      const params = {
        page,
        limit: wellskyPagination.pageSize,
        ...filters
      };

      const result = await getSavedWellskyRecords(params);
      setWellskyData(result.records);
      setWellskyPagination(prev => ({
        ...prev,
        current: page,
        total: result.pagination.total
      }));
    } catch (error) {
      notification.error({
        message: 'Error Loading WellSky Data',
        description: error.message,
        placement: 'topRight'
      });
    } finally {
      setWellskyLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadHubspotData();
    loadWellskyData();
  }, []);

  // HubSpot table columns
  const hubspotColumns = [
    {
      title: 'Deal ID',
      dataIndex: 'recordId',
      key: 'recordId',
      width: 120,
      sorter: true,
      render: (text) => <code>{text}</code>
    },
    {
      title: 'Deal Name',
      dataIndex: 'dealName',
      key: 'dealName',
      width: 200,
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Full Name',
      dataIndex: 'fullName',
      key: 'fullName',
      width: 180,
      sorter: true,
      ellipsis: true,
      render: (text, record) => {
        // Fallback to constructing from first/last name if fullName is empty
        const fullName = text || `${record.responsiblePartyFirstName || ''} ${record.responsiblePartyLastName || ''}`.trim();
        return fullName || 'N/A';
      }
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 150,
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Client Phone',
      dataIndex: 'clientPhone',
      key: 'clientPhone',
      width: 130,
      sorter: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Rep Phone',
      dataIndex: 'responsiblePartyPhone',
      key: 'responsiblePartyPhone',
      width: 130,
      sorter: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Client Email',
      dataIndex: 'clientEmail',
      key: 'clientEmail',
      width: 180,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Rep Email',
      dataIndex: 'responsiblePartyEmail',
      key: 'responsiblePartyEmail',
      width: 180,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Sales Rep',
      dataIndex: 'insideSalesRep',
      key: 'insideSalesRep',
      width: 120,
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Deal Stage',
      dataIndex: 'dealStage',
      key: 'dealStage',
      width: 140,
      sorter: true,
      render: (text) => <Tag color="blue">{text}</Tag>
    },
    {
      title: 'Close Date',
      dataIndex: 'closeDate',
      key: 'closeDate',
      width: 110,
      sorter: true,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A'
    },
    {
      title: 'Pipeline',
      dataIndex: 'pipeline',
      key: 'pipeline',
      width: 140,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Commission Eligible',
      dataIndex: 'commissionEligible',
      key: 'commissionEligible',
      width: 120,
      sorter: true,
      render: (text) => (
        <Tag color={text === 'Yes' ? 'green' : text === 'No' ? 'red' : 'default'}>
          {text || 'Unknown'}
        </Tag>
      )
    },
    {
      title: 'Client Type',
      dataIndex: 'clientType',
      key: 'clientType',
      width: 120,
      sorter: true,
      render: (text) => text ? <Tag color="purple">{text}</Tag> : 'N/A'
    },
    {
      title: 'Assessment Address',
      key: 'assessmentAddress',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const parts = [
          record.assessmentAddress,
          record.assessmentCity,
          record.assessmentState,
          record.assessmentPostalCode
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'N/A';
      }
    },
    {
      title: 'Service Address',
      key: 'serviceAddress',
      width: 200,
      ellipsis: true,
      render: (_, record) => {
        const parts = [
          record.serviceAddress,
          record.serviceCity,
          record.serviceState,
          record.servicePostalCode
        ].filter(Boolean);
        return parts.length > 0 ? parts.join(', ') : 'N/A';
      }
    },
    {
      title: 'WellSky ID',
      dataIndex: 'wellskyDatabaseId',
      key: 'wellskyDatabaseId',
      width: 100,
      sorter: true,
      render: (text) => text ? <code>{text}</code> : <Tag color="orange">None</Tag>
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      sorter: true,
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  // WellSky table columns
  const wellskyColumns = [
    {
      title: 'Database ID',
      dataIndex: 'databaseId',
      key: 'databaseId',
      width: 120,
      sorter: true,
      render: (text) => <code>{text}</code>
    },
    {
      title: 'Client Name',
      dataIndex: 'clientName',
      key: 'clientName',
      width: 150,
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Email',
      dataIndex: 'clientEmail',
      key: 'clientEmail',
      width: 180,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Address',
      dataIndex: 'clientAddress',
      key: 'clientAddress',
      width: 200,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'City',
      dataIndex: 'clientCity',
      key: 'clientCity',
      width: 120,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'State',
      dataIndex: 'clientState',
      key: 'clientState',
      width: 80,
      sorter: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Postal Code',
      dataIndex: 'clientZip',
      key: 'clientZip',
      width: 100,
      sorter: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Spouse Name',
      dataIndex: 'spouseName',
      key: 'spouseName',
      width: 130,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Responsible Party',
      dataIndex: 'responsiblePartyName',
      key: 'responsiblePartyName',
      width: 150,
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Sales Rep',
      dataIndex: 'salesRep',
      key: 'salesRep',
      width: 120,
      sorter: true,
      ellipsis: true
    },
    {
      title: 'Service Type',
      dataIndex: 'serviceType',
      key: 'serviceType',
      width: 120,
      sorter: true,
      render: (text) => text ? <Tag color="purple">{text}</Tag> : '-'
    },
    {
      title: 'Start Date',
      dataIndex: 'serviceStartDate',
      key: 'serviceStartDate',
      width: 100,
      sorter: true,
      render: (date) => date ? new Date(date).toLocaleDateString() : '-'
    },
    {
      title: 'Monthly Revenue',
      dataIndex: 'monthlyRevenue',
      key: 'monthlyRevenue',
      width: 120,
      sorter: true,
      render: (amount) => amount ? `$${amount.toLocaleString()}` : '-'
    },
    {
      title: 'Agency',
      dataIndex: 'agencySubdomain',
      key: 'agencySubdomain',
      width: 120,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Marketer',
      dataIndex: 'marketer',
      key: 'marketer',
      width: 120,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Client Referrer',
      dataIndex: 'clientReferrer',
      key: 'clientReferrer',
      width: 130,
      sorter: true,
      ellipsis: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Referral Source',
      dataIndex: 'referralSourceType',
      key: 'referralSourceType',
      width: 130,
      sorter: true,
      ellipsis: true,
      render: (text) => text ? <Tag color="cyan">{text}</Tag> : 'N/A'
    },
    {
      title: 'Mobile Phone',
      dataIndex: 'mobilePhone',
      key: 'mobilePhone',
      width: 120,
      sorter: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Home Phone',
      dataIndex: 'homePhone',
      key: 'homePhone',
      width: 120,
      sorter: true,
      render: (text) => text || 'N/A'
    },
    {
      title: 'Status',
      dataIndex: 'deactivated',
      key: 'deactivated',
      width: 80,
      sorter: true,
      render: (deactivated) => (
        <Tag color={deactivated ? 'red' : 'green'}>
          {deactivated ? 'Inactive' : 'Active'}
        </Tag>
      )
    },
    {
      title: 'Date Deactivated',
      dataIndex: 'dateDeactivated',
      key: 'dateDeactivated',
      width: 120,
      sorter: true,
      render: (date) => date ? new Date(date).toLocaleDateString() : 'N/A'
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      sorter: true,
      render: (date) => new Date(date).toLocaleDateString()
    }
  ];

  const handleHubspotSearch = () => {
    loadHubspotData(1, hubspotFilters);
  };

  const handleWellskySearch = () => {
    loadWellskyData(1, wellskyFilters);
  };

  const handleHubspotTableChange = (pagination, filters, sorter) => {
    const params = { ...hubspotFilters };
    
    // Handle sorting
    if (sorter && sorter.field) {
      params.sortBy = sorter.field;
      params.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }
    
    loadHubspotData(pagination.current, params);
  };

  const handleWellskyTableChange = (pagination, filters, sorter) => {
    const params = { ...wellskyFilters };
    
    // Handle sorting
    if (sorter && sorter.field) {
      params.sortBy = sorter.field;
      params.sortOrder = sorter.order === 'ascend' ? 'asc' : 'desc';
    }
    
    loadWellskyData(pagination.current, params);
  };

  return (
    <Layout style={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      <Content style={{ padding: '16px 24px' }}>
        <div style={{ width: '100%' }}>
          <Tabs defaultActiveKey="hubspot" size="large">
            {/* HubSpot Data Tab */}
            <TabPane tab="HubSpot Deals" key="hubspot">
              <Card>
                {/* Filters */}
                <div style={{ marginBottom: 16 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6}>
                      <Input
                        placeholder="Search deals..."
                        prefix={<SearchOutlined />}
                        value={hubspotFilters.search}
                        onChange={(e) => setHubspotFilters(prev => ({ ...prev, search: e.target.value }))}
                        onPressEnter={handleHubspotSearch}
                        style={{ height: '48px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Select
                        placeholder="Sales Rep"
                        style={{ width: '100%', height: '48px' }}
                        value={hubspotFilters.salesRep || undefined}
                        onChange={(value) => setHubspotFilters(prev => ({ ...prev, salesRep: value }))}
                        allowClear
                      >
                        <Option value="Tyson Queen">Tyson Queen</Option>
                        <Option value="Irvin Trahan III">Irvin Trahan III</Option>
                        <Option value="Charity Grandchampt">Charity Grandchampt</Option>
                        <Option value="Joel Thomas">Joel Thomas</Option>
                        <Option value="Joshua Thomas">Joshua Thomas</Option>
                        <Option value="Crystal Robbins">Crystal Robbins</Option>
                        <Option value="Casey Jones">Casey Jones</Option>
                        <Option value="Rayvion Williams">Rayvion Williams</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Select
                        placeholder="Commission Eligible"
                        style={{ width: '100%', height: '48px' }}
                        value={hubspotFilters.commissionEligible || undefined}
                        onChange={(value) => setHubspotFilters(prev => ({ ...prev, commissionEligible: value }))}
                        allowClear
                      >
                        <Option value="Yes">Yes</Option>
                        <Option value="No">No</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Select
                        placeholder="Client Type"
                        style={{ width: '100%', height: '48px' }}
                        value={hubspotFilters.clientType || undefined}
                        onChange={(value) => setHubspotFilters(prev => ({ ...prev, clientType: value }))}
                        allowClear
                      >
                        <Option value="New Client">New Client</Option>
                        <Option value="Existing Client">Existing Client</Option>
                        <Option value="Referral">Referral</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Select
                        placeholder="Pipeline"
                        style={{ width: '100%', height: '48px' }}
                        value={hubspotFilters.pipeline || undefined}
                        onChange={(value) => setHubspotFilters(prev => ({ ...prev, pipeline: value }))}
                        allowClear
                      >
                        <Option value="Lead Acquisition Pipeline">Lead Acquisition Pipeline</Option>
                        <Option value="default">Default</Option>
                      </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Row gutter={[8, 0]}>
                        <Col span={12}>
                          <Button 
                            type="primary" 
                            icon={<SearchOutlined />} 
                            onClick={handleHubspotSearch}
                            style={{ height: '48px', width: '100%' }}
                          >
                            Search
                          </Button>
                        </Col>
                        <Col span={12}>
                          <Button 
                            icon={<ReloadOutlined />} 
                            onClick={() => loadHubspotData()}
                            style={{ height: '48px', width: '100%' }}
                          >
                            Refresh
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>

                {/* Table */}
                <Table
                  columns={hubspotColumns}
                  dataSource={hubspotData}
                  rowKey="recordId"
                  loading={hubspotLoading}
                  pagination={{
                    ...hubspotPagination,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} deals`
                  }}
                  onChange={handleHubspotTableChange}
                  scroll={{ x: 'max-content' }}
                />
              </Card>
            </TabPane>

            {/* WellSky Data Tab */}
            <TabPane tab="WellSky Records" key="wellsky">
              <Card>
                {/* Filters */}
                <div style={{ marginBottom: 16 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={7}>
                      <Input
                        placeholder="Search records..."
                        prefix={<SearchOutlined />}
                        value={wellskyFilters.search}
                        onChange={(e) => setWellskyFilters(prev => ({ ...prev, search: e.target.value }))}
                        onPressEnter={handleWellskySearch}
                        style={{ height: '48px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Input
                        placeholder="Sales Rep"
                        value={wellskyFilters.salesRep}
                        onChange={(e) => setWellskyFilters(prev => ({ ...prev, salesRep: e.target.value }))}
                        style={{ height: '48px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Input
                        placeholder="Service Type"
                        value={wellskyFilters.serviceType}
                        onChange={(e) => setWellskyFilters(prev => ({ ...prev, serviceType: e.target.value }))}
                        style={{ height: '48px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Input
                        placeholder="Agency"
                        value={wellskyFilters.agencySubdomain}
                        onChange={(e) => setWellskyFilters(prev => ({ ...prev, agencySubdomain: e.target.value }))}
                        style={{ height: '48px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={3}>
                      <Input
                        placeholder="Marketer"
                        value={wellskyFilters.marketer}
                        onChange={(e) => setWellskyFilters(prev => ({ ...prev, marketer: e.target.value }))}
                        style={{ height: '48px' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={5}>
                      <Row gutter={[8, 0]}>
                        <Col span={12}>
                          <Button 
                            type="primary" 
                            icon={<SearchOutlined />} 
                            onClick={handleWellskySearch}
                            style={{ height: '48px', width: '100%' }}
                          >
                            Search
                          </Button>
                        </Col>
                        <Col span={12}>
                          <Button 
                            icon={<ReloadOutlined />} 
                            onClick={() => loadWellskyData()}
                            style={{ height: '48px', width: '100%' }}
                          >
                            Refresh
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </div>

                {/* Table */}
                <Table
                  columns={wellskyColumns}
                  dataSource={wellskyData}
                  rowKey="databaseId"
                  loading={wellskyLoading}
                  pagination={{
                    ...wellskyPagination,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`
                  }}
                  onChange={handleWellskyTableChange}
                  scroll={{ x: 'max-content' }}
                />
              </Card>
            </TabPane>
          </Tabs>
        </div>
      </Content>
    </Layout>
  );
};

export default SavedDataPage;
